import React, { forwardRef } from 'react';
import { type HTMLMotionProps, motion } from 'framer-motion';
import { cls, cn } from '@/lib/utils';

const baseTextSize = `text-base sm:text-lg lg:text-xl`;

//
// Typography H1
//
export type TypographyH1Properties = HTMLMotionProps<'h1'>;

const TypographyH1 = forwardRef<HTMLHeadingElement, TypographyH1Properties>(
  ({ className, children, ...properties }, reference) => (
    <motion.h1
      ref={reference}
      className={cn(
        cls`
          scroll-m-20
          text-3xl sm:text-4xl md:text-5xl lg:text-6xl
          font-medium
          font-prata
          leading-tight lg:leading-[1.2]
        `,
        className,
      )}
      {...properties}
    >
      {children}
    </motion.h1>
  ),
);

TypographyH1.displayName = 'TypographyH1';

//
// Typography H2
//
export type TypographyH2Properties = HTMLMotionProps<'h2'>;

const TypographyH2 = forwardRef<HTMLHeadingElement, TypographyH2Properties>(
  ({ className, children, ...properties }, reference) => (
    <motion.h2
      ref={reference}
      className={cn(
        cls`
          scroll-m-20
          border-b pb-2 first:mt-0
          text-2xl sm:text-3xl lg:text-4xl
          leading-tight lg:leading-[1.3]
          font-semibold
          font-prata
          tracking-tight
        `,
        className,
      )}
      {...properties}
    >
      {children}
    </motion.h2>
  ),
);

TypographyH2.displayName = 'TypographyH2';

//
// Typography H3
//
export type TypographyH3Propertise = HTMLMotionProps<'h3'>;

const TypographyH3 = forwardRef<HTMLHeadingElement, TypographyH3Propertise>(
  ({ className, children, ...properties }, reference) => (
    <motion.h3
      ref={reference}
      className={cn(
        cls`
          scroll-m-20
          text-xl sm:text-2xl md:text-2.5xl lg:text-3xl
          font-semibold
          tracking-wide
          font-rokkitt`,
        className,
      )}
      {...properties}
    >
      {children}
    </motion.h3>
  ),
);

TypographyH3.displayName = 'TypographyH3';

//
// Typography H4
//
export type TypographyH4Properties = HTMLMotionProps<'h4'>;

const TypographyH4 = forwardRef<HTMLHeadingElement, TypographyH4Properties>(
  ({ className, children, ...properties }, reference) => (
    <motion.h4
      ref={reference}
      className={cn(
        cls`
          scroll-m-20
          text-lg sm:text-xl lg:text-2xl
          font-semibold
          tracking-wide
          font-rokkitt`,
        className,
      )}
      {...properties}
    >
      {children}
    </motion.h4>
  ),
);

TypographyH4.displayName = 'TypographyH4';

//
// Typography Paragraph
//
export type TypographyParagraphProperties = HTMLMotionProps<'p'>;

const TypographyP = forwardRef<
  HTMLParagraphElement,
  TypographyParagraphProperties
>(({ className, children, ...properties }, reference) => (
  <motion.p
    ref={reference}
    className={cn(
      cls`
        leading-6 sm:leading-7 md:leading-8 lg:leading-9
        text-base sm:text-lg md:text-xl lg:text-2xl
        not-first:mt-6
        font-firaSans`,
      baseTextSize,
      className,
    )}
    {...properties}
  >
    {children}
  </motion.p>
));

TypographyP.displayName = 'TypographyP';

//
// Typography Blockquote
//
export type TypographyBlockquoteProperties = HTMLMotionProps<'blockquote'>;

const TypographyBlockquote = forwardRef<
  HTMLQuoteElement,
  TypographyBlockquoteProperties
>(({ className, children, ...properties }, reference) => (
  <motion.blockquote
    ref={reference}
    className={cn(
      cls`
        mt-6
        border-l-2
        pl-4
        italic
      `,
      baseTextSize,
      className,
    )}
    {...properties}
  >
    {children}
  </motion.blockquote>
));

TypographyBlockquote.displayName = 'TypographyBlockquote';

//
// Typography Large
//
export type TypographyLargeProperties = HTMLMotionProps<'div'>;

const TypographyLarge = forwardRef<HTMLDivElement, TypographyLargeProperties>(
  ({ className, children, ...properties }, reference) => (
    <motion.div
      ref={reference}
      className={cn(cls`font-semibold`, baseTextSize, className)}
      {...properties}
    >
      {children}
    </motion.div>
  ),
);

TypographyLarge.displayName = 'TypographyLarge';

//
// Typography Small
//
export type TypographySmallProperties = HTMLMotionProps<'small'>;

const TypographySmall = forwardRef<HTMLDivElement, TypographySmallProperties>(
  ({ className, children, ...properties }, reference) => (
    <motion.small
      ref={reference}
      className={cn(
        cls`text-sm sm:text-base font-medium leading-none`,
        className,
      )}
      {...properties}
    >
      {children}
    </motion.small>
  ),
);

TypographySmall.displayName = 'TypographySmall';

export {
  TypographyH1,
  TypographyH2,
  TypographyH3,
  TypographyH4,
  TypographyP,
  TypographyBlockquote,
  TypographyLarge,
  TypographySmall,
};
